import { ref, computed, Ref } from 'vue';
import { IntervalDateEnum, TypeCampaignEnum } from '@/types';
import { fetchWorkflowAnalytics } from '@/composables/flows/useFlowApi';

/**
 * Date interval configuration for analytics
 */
export interface AnalyticsDateInterval {
  interval: IntervalDateEnum;
  customDateRange?: Date[];
}

/**
 * Statistics for a single metric
 */
export interface AnalyticsStat {
  nb: number | string;
  percent?: number;
}

/**
 * Global statistics for an operator (journey/parcours)
 */
export interface GlobalStatistics {
  entered: AnalyticsStat;
  passed: AnalyticsStat;
  stopped: AnalyticsStat;
  paused: AnalyticsStat;
  blockedByMarketingPressure: AnalyticsStat;
  finished: AnalyticsStat;
  blockNoCreditsSms: AnalyticsStat;
  /**
   * Motifs d'arrêt exposés en complément : ils comptaient déjà dans `entered`
   * mais n'apparaissaient dans aucune ligne, d'où des totaux qui ne tombaient
   * pas juste. Optionnels, le temps que l'API déployée les renvoie.
   */
  blockedByPlanLimit?: AnalyticsStat;
  campaignInactive?: AnalyticsStat;
  blockedByDeliverability?: AnalyticsStat;
  blockedByPersistence?: AnalyticsStat;
  blockedByMaxDelay?: AnalyticsStat;
  internalError?: AnalyticsStat;
  outOfDateRange?: AnalyticsStat;
  cartAlreadyProcessed?: AnalyticsStat;
  campaignDisabled?: AnalyticsStat;
  exited?: AnalyticsStat;
}

/**
 * Send statistics for sending operators (email, sms, etc.)
 */
export interface SendStatistics {
  send: AnalyticsStat;
  open: AnalyticsStat;
  open_unique: AnalyticsStat;
  click: AnalyticsStat;
  click_unique: AnalyticsStat;
  sales: AnalyticsStat;
  sales_amount?: AnalyticsStat;
  unsub: AnalyticsStat;
  bounce: AnalyticsStat;
  spam: AnalyticsStat;
  /**
   * Inscriptions générées par une pop-up ou un modèle Display, comptées
   * séparément : créer un compte et s'abonner à la newsletter ne sont pas le
   * même geste, et un marchand qui travaille l'un ne travaille pas l'autre.
   */
  accountCreations?: AnalyticsStat;
  newsletterSignups?: AnalyticsStat;
}

/**
 * Mode contact uniquement : événements horodatés (ISO 8601) du contact filtré
 * sur un step d'envoi + compteurs cumulés (open/click/order_count). Le rendu
 * front affiche une timeline avec date+heure et le compteur si > 1.
 */
export interface ContactEvents {
  receivedAt?: string;
  openedAt?: string;
  clickedAt?: string;
  orderedAt?: string;
  openCount?: number;
  clickCount?: number;
  orderCount?: number;
}

/**
 * Status sémantique d'un passage de step pour un contact, dérivé côté
 * backend de `workflow_steps_*.status` (cf. STATUS_TO_KEY dans
 * workflow_stats_tree.service.ts).
 */
export type OperatorStepStatus =
  | 'passed'
  | 'stopped'
  | 'finished'
  | 'paused'
  | 'blockedByMarketingPressure'
  | 'blockNoCreditsSms'
  | 'blockedByPlanLimit'
  | 'campaignInactive'
  | 'blockedByDeliverability'
  | 'blockedByPersistence'
  | 'blockedByMaxDelay'
  | 'internalError'
  | 'outOfDateRange'
  | 'cartAlreadyProcessed'
  | 'campaignDisabled';

/**
 * Analytics data for a single operator
 */
export interface OperatorAnalytics {
  globalStatistics?: GlobalStatistics;
  sendStatistics?: SendStatistics;
  /**
   * Mode contact uniquement. ISO 8601 de l'entrée du contact dans l'étape
   * (created_at du row workflow_steps_*).
   */
  passedAt?: string;
  /**
   * Mode contact uniquement. ISO 8601 de la sortie du contact de l'étape
   * (updated_at). Utilisé pour les libellés "Bloqué le ..." / "Validé le ..."
   * sur les boxes filtres (instant où le worker a tranché).
   */
  exitedAt?: string;
  /**
   * Mode contact uniquement. Statut sémantique du passage du contact dans
   * cette étape (passed/stopped/finished/...). Pilote le libellé et la
   * couleur du badge analytics côté front.
   */
  stepStatus?: OperatorStepStatus;
  /**
   * Mode contact + step d'envoi uniquement. Absent sur les ancêtres non-envois.
   */
  contactEvents?: ContactEvents;
}

/**
 * Full analytics response from API
 */
export interface FlowAnalyticsResponse {
  success: boolean;
  statistics: Record<string, OperatorAnalytics>;
}

/**
 * Composable for managing flow analytics state
 * 
 * Provides:
 * - Analytics mode toggle
 * - Analytics data storage per operator
 * - Loading state
 * - Methods to fetch and clear analytics
 */
export function useFlowAnalytics() {
  // Analytics mode state
  const isAnalyticsMode = ref(false);
  const isLoading = ref(false);
  const error: Ref<string | null> = ref(null);

  // Date interval for analytics period
  const dateInterval: Ref<AnalyticsDateInterval> = ref({
    interval: IntervalDateEnum.LAST_30_DAYS,
  });

  // Analytics data keyed by operator ID (e.g., 'created_operator_2')
  const analyticsData: Ref<Record<string, OperatorAnalytics>> = ref({});

  /**
   * Toggle analytics mode on/off
   */
  const toggleAnalyticsMode = () => {
    isAnalyticsMode.value = !isAnalyticsMode.value;
  };

  /**
   * Enable analytics mode
   */
  const enableAnalyticsMode = () => {
    isAnalyticsMode.value = true;
  };

  /**
   * Disable analytics mode
   */
  const disableAnalyticsMode = () => {
    isAnalyticsMode.value = false;
  };

  /**
   * Set analytics data from API response
   */
  const setAnalyticsData = (data: Record<string, OperatorAnalytics>) => {
    analyticsData.value = data;
  };

  /**
   * Get analytics for a specific operator
   */
  const getOperatorAnalytics = (operatorId: string): OperatorAnalytics | null => {
    return analyticsData.value[operatorId] || null;
  };

  /**
   * Check if an operator has global statistics
   */
  const hasGlobalStatistics = (operatorId: string): boolean => {
    const data = analyticsData.value[operatorId];
    return !!data?.globalStatistics;
  };

  /**
   * Check if an operator has send statistics
   */
  const hasSendStatistics = (operatorId: string): boolean => {
    const data = analyticsData.value[operatorId];
    return !!data?.sendStatistics;
  };

  /**
   * Clear all analytics data
   */
  const clearAnalytics = () => {
    analyticsData.value = {};
    error.value = null;
  };

  /**
   * Set loading state
   */
  const setLoading = (loading: boolean) => {
    isLoading.value = loading;
  };

  /**
   * Set error state
   */
  const setError = (err: string | null) => {
    error.value = err;
  };

  /**
   * Set date interval for analytics period
   */
  const setDateInterval = (interval: IntervalDateEnum, customDateRange?: Date[]) => {
    dateInterval.value = {
      interval,
      customDateRange,
    };
  };

  /**
   * Load analytics data from API
   * @param idShop Shop ID
   * @param typeCampaign Campaign type
   * @param idCampaign Campaign ID
   * @returns true if data was loaded, false if no data available
   */
  const loadAnalyticsData = async (
    idShop: number,
    typeCampaign: TypeCampaignEnum,
    idCampaign: number,
  ): Promise<boolean> => {
    setLoading(true);
    setError(null);

    try {
      const data = await fetchWorkflowAnalytics(
        idShop,
        typeCampaign,
        idCampaign,
        dateInterval.value,
      );

      if (data && Object.keys(data).length > 0) {
        setAnalyticsData(data);
        return true;
      }

      // No data available
      clearAnalytics();
      return false;
    } catch (err) {
      console.error('Error loading analytics data:', err);
      setError('Failed to load analytics data');
      clearAnalytics();
      return false;
    } finally {
      setLoading(false);
    }
  };

  /**
   * Load mock data for testing
   */
  const loadMockData = () => {
    const baseSendStats: SendStatistics = {
      send: { nb: 1250 },
      open: { nb: 875, percent: 70 },
      open_unique: { nb: 650, percent: 52 },
      click: { nb: 125, percent: 10 },
      click_unique: { nb: 95, percent: 7.6 },
      sales: { nb: 12, percent: 0.96 },
      sales_amount: { nb: '€1,234.56' },
      unsub: { nb: 3, percent: 0.24 },
      bounce: { nb: 8, percent: 0.64 },
      spam: { nb: 1, percent: 0.08 },
    };

    const baseGlobalStats: GlobalStatistics = {
      entered: { nb: 150 },
      passed: { nb: 142, percent: 94.67 },
      stopped: { nb: 3, percent: 2 },
      paused: { nb: 2, percent: 1.33 },
      blockedByMarketingPressure: { nb: 2, percent: 1.33 },
      finished: { nb: 1, percent: 0.67 },
      blockNoCreditsSms: { nb: 0, percent: 0 },
    };

    const mockData: Record<string, OperatorAnalytics> = {};

    // Generate mock data for operators 1-20 to cover most flows
    for (let i = 1; i <= 20; i++) {
      mockData[`created_operator_${i}`] = {
        globalStatistics: { ...baseGlobalStats },
        sendStatistics: i % 2 === 0 ? { ...baseSendStats } : undefined,
      };
    }

    setAnalyticsData(mockData);
  };

  return {
    // State
    isAnalyticsMode,
    isLoading,
    error,
    analyticsData,
    dateInterval,

    // Mode controls
    toggleAnalyticsMode,
    enableAnalyticsMode,
    disableAnalyticsMode,

    // Data management
    setAnalyticsData,
    getOperatorAnalytics,
    hasGlobalStatistics,
    hasSendStatistics,
    clearAnalytics,
    setLoading,
    setError,
    loadMockData,
    loadAnalyticsData,

    // Date interval
    setDateInterval,
  };
}

// Singleton instance for global state
let analyticsInstance: ReturnType<typeof useFlowAnalytics> | null = null;

/**
 * Get the global analytics instance (singleton pattern)
 * Use this when you need to share analytics state across components
 */
export function useGlobalFlowAnalytics(): ReturnType<typeof useFlowAnalytics> {
  if (!analyticsInstance) {
    analyticsInstance = useFlowAnalytics();
  }
  return analyticsInstance;
}

/**
 * Reset the global analytics instance
 * Useful for testing or when switching flows
 */
export function resetGlobalFlowAnalytics(): void {
  if (analyticsInstance) {
    analyticsInstance.clearAnalytics();
    analyticsInstance.disableAnalyticsMode();
  }
  analyticsInstance = null;
}
